import { createSlice } from '@reduxjs/toolkit';
import { loadShowMenu, saveShowMenu } from 'utils/menuPersistence';
import { loadPageGuidesDismissed, saveAllPageGuidesDismissed, saveDismissedPageGuide, } from 'utils/pageGuidesPersistence';
const initialState = {
    theme: localStorage.getItem('theme') || 'light',
    specificationsDashboard: 'grid',
    specPreviewComponents: 'grid',
    orderCollectionsTimelinePeriod: 'week',
    showMenu: loadShowMenu(),
    breadcrumbs: [],
    showModals: [],
    showSidebars: [],
    pageGuides: Object.assign(Object.assign({}, loadPageGuidesDismissed()), { openPageKey: null }),
    helpAgentOpen: false,
    nextLayer: 1,
};
const layoutSlice = createSlice({
    name: 'layout',
    initialState,
    reducers: {
        setTheme: (state, action) => {
            state.theme = action.payload;
        },
        toggleTheme: (state) => {
            state.theme = state.theme === 'light' ? 'dark' : 'light';
        },
        setShowMenu: (state, action) => {
            state.showMenu = action.payload;
            saveShowMenu(state.showMenu);
        },
        toggleShowMenu: (state) => {
            state.showMenu = !state.showMenu;
            saveShowMenu(state.showMenu);
        },
        setSpecificationsDashboard: (state, action) => {
            state.specificationsDashboard = action.payload;
        },
        toggleSpecificationsDashboard: (state) => {
            state.specificationsDashboard = state.specificationsDashboard === 'list' ? 'grid' : 'list';
        },
        toggleSpecPreviewComponents: (state) => {
            state.specPreviewComponents = state.specPreviewComponents === 'list' ? 'grid' : 'list';
        },
        setOrderCollectionsTimelinePeriod: (state, action) => {
            state.orderCollectionsTimelinePeriod = action.payload;
        },
        // breadcrumbs actions
        setBreadcrumbs: (state, action) => {
            state.breadcrumbs = action.payload;
        },
        clearBreadcrumbs: (state) => {
            state.breadcrumbs = [];
        },
        // modal actions
        openModal: (state, action) => {
            const existingIndex = state.showModals.findIndex((modal) => modal.name === action.payload.name);
            if (existingIndex === -1) {
                state.showModals.push(Object.assign(Object.assign({}, action.payload), { opened: true, active: false, layer: state.nextLayer }));
                state.nextLayer += 1;
            }
            else {
                state.showModals[existingIndex] = Object.assign(Object.assign(Object.assign({}, state.showModals[existingIndex]), action.payload), { opened: true, active: true, layer: state.nextLayer });
                state.nextLayer += 1;
            }
        },
        activateModal: (state, action) => {
            const modal = state.showModals.find((modal) => modal.name === action.payload.name);
            if (modal) {
                modal.active = true;
            }
        },
        closeModal: (state, action) => {
            const modal = state.showModals.find((modal) => modal.name === action.payload.name);
            if (modal) {
                modal.active = false;
            }
        },
        removeModal: (state, action) => {
            state.showModals = state.showModals.filter((modal) => modal.name !== action.payload.name);
        },
        // sidebar actions
        openSidebar: (state, action) => {
            const existingIndex = state.showSidebars.findIndex((sidebar) => sidebar.name === action.payload.name);
            if (existingIndex === -1) {
                state.showSidebars.push(Object.assign(Object.assign({}, action.payload), { opened: true, active: false, layer: state.nextLayer }));
                state.nextLayer += 1;
            }
            else {
                state.showSidebars[existingIndex] = Object.assign(Object.assign(Object.assign({}, state.showSidebars[existingIndex]), action.payload), { opened: true, active: true, layer: state.nextLayer });
                state.nextLayer += 1;
            }
        },
        activateSidebar: (state, action) => {
            const sidebar = state.showSidebars.find((sidebar) => sidebar.name === action.payload.name);
            if (sidebar) {
                sidebar.active = true;
            }
        },
        closeSidebar: (state, action) => {
            const sidebar = state.showSidebars.find((sidebar) => sidebar.name === action.payload.name);
            if (sidebar) {
                sidebar.active = false;
            }
        },
        removeSidebar: (state, action) => {
            state.showSidebars = state.showSidebars.filter((sidebar) => sidebar.name !== action.payload.name);
        },
        closeAllSidebars: (state) => {
            state.showSidebars = state.showSidebars.map((sidebar) => (Object.assign(Object.assign({}, sidebar), { active: false })));
        },
        removeAllSidebars: (state) => {
            state.showSidebars = [];
        },
        // page guide actions
        openPageGuide: (state, action) => {
            state.pageGuides.openPageKey = action.payload;
        },
        closePageGuide: (state) => {
            state.pageGuides.openPageKey = null;
        },
        dismissPageGuide: (state, action) => {
            const next = saveDismissedPageGuide(action.payload);
            state.pageGuides.dismissedPages = next.dismissedPages;
            state.pageGuides.openPageKey = null;
        },
        dismissAllPageGuides: (state) => {
            const next = saveAllPageGuidesDismissed();
            state.pageGuides.allDismissed = next.allDismissed;
            state.pageGuides.openPageKey = null;
        },
        // help agent actions
        openHelpAgent: (state) => {
            state.helpAgentOpen = true;
        },
        closeHelpAgent: (state) => {
            state.helpAgentOpen = false;
        },
    },
});
export const { setTheme, toggleTheme, setShowMenu, toggleShowMenu, setSpecificationsDashboard, toggleSpecificationsDashboard, toggleSpecPreviewComponents, setOrderCollectionsTimelinePeriod, setBreadcrumbs, clearBreadcrumbs, openModal, activateModal, closeModal, removeModal, openSidebar, activateSidebar, closeSidebar, removeSidebar, closeAllSidebars, removeAllSidebars, openPageGuide, closePageGuide, dismissPageGuide, dismissAllPageGuides, openHelpAgent, closeHelpAgent, } = layoutSlice.actions;
export const selectLayout = (state) => state.layout;
export const selectOrderCollectionsTimelinePeriod = (state) => state.layout.orderCollectionsTimelinePeriod;
export const selectModal = (state) => state.layout.showModals;
export const selectModals = (state) => state.layout.showModals;
export const selectModalByName = (name) => (state) => state.layout.showModals.find((modal) => modal.name === name);
export const selectSidebars = (state) => state.layout.showSidebars;
export const selectSidebarByName = (name) => (state) => state.layout.showSidebars.find((sidebar) => sidebar.name === name);
export const selectBreadcrumbs = (state) => state.layout.breadcrumbs;
export const selectPageGuides = (state) => state.layout.pageGuides;
export const selectHelpAgentOpen = (state) => state.layout.helpAgentOpen;
export default layoutSlice.reducer;
